import { defineStore } from 'pinia';
import { usePinia } from '#imports';
import { handleErrorAndNotificationHandler } from '~/util/errorNotificationHandler';
import type { Pinia } from './shared/sharedTypes';
import type {
  TeamContact,
  TableToReturnTo,
} from '~/stores/helpers/helpersTypes';

export const ENTITY_TYPES_ENUM = {
  organization: 'ORGANIZATION',
  studyTeam: 'STUDY_TEAM',
  user: 'USER',
  account: 'ACCOUNT',
  contact: 'CONTACT',
};

export const LIST_TYPES_ENUM = {
  service: 'SERVICE',
  address: 'ADDRESS',
  phone: 'PHONE',
  website: 'WEBSITE',
  prefix: 'PREFIX',
  emailAddress: 'EMAIL_ADDRESS',
  status: 'STATUS',
  suffix: 'SUFFIX',
};

export const POST_DETAIL_REFERRER_ENUM = {
  channel: 'CHANNEL',
  unreadPosts: 'UNREAD_POSTS',
  classifications: 'CLASSIFICATIONS',
};

export const SPECIAL_CHARACTERS = [
  '@',
  '!',
  '#',
  '%',
  '&',
  '*',
  '+',
  '[]',
  '{}',
  '{',
  '}',
  '[',
  ']',
];

export const MAX_STUDY_NAME_LENGTH = 150;

export const MIN_STUDY_NAME_LENGTH = 3;

interface VariantDefinition {
  name: string;
  weight: number;
  stickiness?: string;
  payload?: {
    type: string;
    value: string;
  };
  overrides?: unknown[];
}

interface ListTypeData {
  entityName: string;
  listTypeId: number;
  listTypeName: string;
  name: string;
}

interface CountryTypeData {
  countryId: number;
  name: string;
  positionIndex: number;
  roleId: number;
  threeDigitCode: string;
  twoDigitCode: string;
}

interface FeatureFlagsData {
  description: string;
  enabled: boolean;
  id?: number;
  key: string;
  uuid: string;
  impressionData?: boolean;
  variants?: VariantDefinition[];
}

interface NotificationStatusTypesData {
  displayText: string;
  serviceEntityTypeStatusId: number;
}

interface TypeFilter {
  entity: string;
  listType: string;
}

interface PostCreate {
  text?: string;
  icon?: string;
  id?: number;
  postType?: string;
}

interface LatestResposeStatus {
  status?: number;
  statusText?: string;
  headers?: HeadersData;
  refetchInvitedTable?: boolean;
}

interface HeadersData {
  'cache-control': string;
  'content-length': string;
  'content-type': string;
}

interface ProfileImg {
  url?: string;
  alt?: string;
  [key: string]: string | undefined;
}

interface ClassificationCategory {
  id: number;
  name: string;
}

interface TermsOfServiceResponse {
  content: string;
}

interface HelpersState {
  listTypeData: ListTypeData[];
  countryData: CountryTypeData[];
  featureFlags: FeatureFlagsData[];
  scrollPosition: number;
  scrollPositionClassifications: number;
  postDetailReferrer: 'CHANNEL' | string;
  cardDetailHasThumbnails: boolean;
  teamId: string;
  routeName: string;
  breadCrumbsLink: boolean;
  teamContact: TeamContact;
  emailAddress: string;
  siteName: string;
  selection: string;
  updateOption: string;
  postCreate: PostCreate;
  activityView: boolean;
  cardIndex: number;
  loader: {
    show: boolean;
  };
  patchedComplianceRule: string;
  patchedCompliance: boolean;
  classPatched: boolean;
  classificationCategories: ClassificationCategory[];
  sharingTeamIds: string[];
  sharingPatched: boolean;
  sideBarCollapsed: boolean;
  postCreationPostIds: number[];
  profileImg: ProfileImg;
  avatarImgFetched: boolean;
  hasViewed: boolean;
  openInviteDialog: boolean;
  openStudySite: boolean;
  openOtherTeam: boolean;
  tableToReturnTo: TableToReturnTo;
  latestResponseStatus: LatestResposeStatus;
  refetchInvitedTable: boolean;
  notificationStatusTypes: NotificationStatusTypesData[];
  changedEmailAddress: string;
  allRolesSelected: string[];
  publicTos: string;
  popupTriggered: boolean;
}

interface HelperAPI {
  user: {
    profileImage(): string;
  };
  app: {
    termsOfService: string;
  };
}

export const useHelperStore = defineStore('helper', {
  state: (): HelpersState => ({
    listTypeData: [],
    countryData: [],
    featureFlags: [],
    scrollPosition: 0,
    scrollPositionClassifications: 0,
    postDetailReferrer: 'CHANNEL',
    cardDetailHasThumbnails: false,
    teamId: '',
    routeName: '',
    breadCrumbsLink: false,
    teamContact: {} as TeamContact,
    emailAddress: '',
    siteName: '',
    selection: '',
    updateOption: '',
    postCreate: {},
    activityView: false,
    cardIndex: 0,
    loader: { show: false },
    patchedComplianceRule: '',
    patchedCompliance: false,
    classPatched: false,
    classificationCategories: [],
    sharingTeamIds: [],
    sharingPatched: false,
    sideBarCollapsed: false,
    postCreationPostIds: [],
    profileImg: {},
    avatarImgFetched: false,
    hasViewed: false,
    openInviteDialog: false,
    openStudySite: false,
    openOtherTeam: false,
    tableToReturnTo: {},
    latestResponseStatus: {},
    refetchInvitedTable: false,
    notificationStatusTypes: [],
    changedEmailAddress: '',
    allRolesSelected: [],
    publicTos: '',
    popupTriggered: true,
  }),

  actions: {
    setLatestResponseStatus(status: string): void {
      try {
        this.latestResponseStatus = JSON.parse(status) as LatestResposeStatus;
      } catch (e) {
        // not a valid json, happens from interceptor registered in store plugin
        // not really relevant, as we use it for auth only
        if (import.meta.dev) {
          console.debug(
            'Failed to parse latest response status:',
            e instanceof Error ? e.message : 'Unknown error'
          );
        }
      }
    },
    updateHasViewedFile(payload: boolean): void {
      this.hasViewed = payload;
    },
    async fetchProfileImg(): Promise<void> {
      const pinia = usePinia() as Pinia<HelperAPI>;
      try {
        const resp = await pinia.$axios.$get(pinia.$API.user.profileImage());
        this.profileImg = resp as ProfileImg;
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error fetching profile image:',
          error
        );
      }
    },
    imgFetched(payload: boolean): void {
      this.avatarImgFetched = payload;
    },
    updateLoader(payload: Partial<{ show: boolean }>): void {
      this.loader = { ...this.loader, ...payload };
    },
    triggerPopup(payload: boolean): void {
      this.popupTriggered = payload;
    },
    syncListTypeData(listTypeData: ListTypeData[]): void {
      this.listTypeData = listTypeData;
    },
    syncfeatureFlags(featureFlagsData: FeatureFlagsData[]): void {
      this.featureFlags = featureFlagsData;
    },
    syncChannelFeatureFlags(featureFlagsData: Record<string, boolean>): void {
      const updatedFlags = this.featureFlags.map((flag) => {
        if (featureFlagsData[flag.key] !== undefined) {
          return { ...flag, enabled: Boolean(featureFlagsData[flag.key]) };
        }
        return flag;
      });

      this.featureFlags = updatedFlags;
    },
    syncNotificationStatusTypes(
      notificationStatusTypesData: NotificationStatusTypesData[]
    ): void {
      this.notificationStatusTypes = notificationStatusTypesData;
    },
    resetWizard(): void {
      this.postCreate = {};
      this.emailAddress = '';
      this.patchedComplianceRule = '';
      this.patchedCompliance = false;
      this.sharingPatched = false;
    },
    syncCountryData(countryData: CountryTypeData[]): void {
      this.countryData = countryData
        .map((c) => {
          const name = c.name;
          if (name.startsWith('country.')) {
            const countryName = name.substring('country.'.length);
            const updateCountryName = countryName
              .replace(/-/g, ' ')
              .split(' ')
              .map(
                (word: string) => word.charAt(0).toUpperCase() + word.slice(1)
              )
              .join(' ');

            return {
              ...c,
              name: updateCountryName,
            };
          } else {
            return { ...c };
          }
        })
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    getType(types: TypeFilter): ListTypeData[] {
      return this.listTypeData.filter(
        (t) =>
          t.entityName === types.entity && t.listTypeName === types.listType
      );
    },
    setScrollPosition(scrollPosition: number) {
      this.scrollPosition = scrollPosition;
    },
    setScrollPositionClassifications(scrollPositionClassifications: number) {
      this.scrollPositionClassifications = scrollPositionClassifications;
    },
    savePostDetailReferrer(referrerPage: string) {
      this.postDetailReferrer = referrerPage;
    },
    setchangedEmailAddress(payload: string): void {
      this.changedEmailAddress = payload;
    },
    setCardDetailHasThumbnails(hasThumbnails: boolean) {
      this.cardDetailHasThumbnails = hasThumbnails;
    },
    setTeamId(teamId: string): void {
      this.teamId = teamId;
    },
    setRouteName(routeName: string) {
      this.routeName = routeName;
    },
    viewActivity(activityView: boolean) {
      this.activityView = activityView;
    },
    breadcrumbsLink(breadCrumbsLink: boolean) {
      this.breadCrumbsLink = breadCrumbsLink;
    },
    setTeamContact(teamContact: TeamContact): void {
      this.teamContact = teamContact;
    },
    storeEmailAddress(emailAddress: string) {
      this.emailAddress = emailAddress;
    },
    setStudySiteTeamName(siteName: string): void {
      this.siteName = siteName;
    },
    selectedValue(selection: string) {
      this.selection = selection;
    },
    updateOptionFn(updateOption: string) {
      this.updateOption = updateOption;
    },
    setAllRolesSelected(payload: string[]): void {
      this.allRolesSelected = payload;
    },

    postCreationWizard(postCreate: PostCreate): void {
      this.postCreate = postCreate;
    },
    summaryCardIndex(cardIndex: number) {
      this.cardIndex = cardIndex;
    },

    async publicTermsOfService(): Promise<void> {
      const pinia = usePinia() as Pinia<HelperAPI>;
      try {
        const resp = await pinia.$axios.$get<TermsOfServiceResponse>(
          pinia.$API.app.termsOfService
        );
        this.publicTos = resp.content;
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error fetching public terms of service:',
          error
        );
      }
    },

    postCreationCompliancePatched(payload: boolean): void {
      this.patchedCompliance = payload;
    },

    postCreationComplianceRules(payload: string) {
      this.patchedComplianceRule = payload;
    },

    classificationPostPatched(payload: boolean): void {
      this.classPatched = payload;
    },

    classificationItems(payload: ClassificationCategory[]): void {
      this.classificationCategories = payload;
    },

    sharingScreenUpdated(payload: string[]): void {
      this.sharingTeamIds = payload;
    },

    setSharingPatched(payload: boolean): void {
      this.sharingPatched = payload;
    },

    collapseSideBar(payload: boolean): void {
      this.sideBarCollapsed = payload;
    },

    setPostCreationPostIds(payload: number[]): void {
      this.postCreationPostIds = payload;
    },

    openInviteMemberDialog(payload: boolean): void {
      this.openInviteDialog = payload;
    },

    openStudySiteCreation(payload: boolean): void {
      this.openStudySite = payload;
    },

    openOtherTeamCreation(payload: boolean): void {
      this.openOtherTeam = payload;
    },

    returnToTable(payload: TableToReturnTo): void {
      this.tableToReturnTo = payload;
    },

    clearReturnToTable(): void {
      this.tableToReturnTo = {} as TableToReturnTo;
    },

    refetchInvitedMemberTable(payload: boolean) {
      this.refetchInvitedTable = payload;
    },
  },

  getters: {
    servicesGetter: (state): ListTypeData[] =>
      state.listTypeData.filter(
        (l) => l.listTypeName === LIST_TYPES_ENUM.service
      ),

    addressGetter: (state): ListTypeData[] =>
      state.listTypeData.filter(
        (l) =>
          l.listTypeName === LIST_TYPES_ENUM.address &&
          l.entityName === ENTITY_TYPES_ENUM.user
      ),

    phoneGetter: (state): ListTypeData[] =>
      state.listTypeData.filter(
        (l) => l.listTypeName === LIST_TYPES_ENUM.phone
      ),

    websiteGetter: (state): ListTypeData[] =>
      state.listTypeData.filter(
        (l) => l.listTypeName === LIST_TYPES_ENUM.website
      ),

    prefixGetter: (state): ListTypeData[] =>
      state.listTypeData.filter(
        (l) =>
          l.listTypeName === LIST_TYPES_ENUM.prefix &&
          l.entityName === ENTITY_TYPES_ENUM.user
      ),

    emailAddressGetter: (state): ListTypeData[] =>
      state.listTypeData.filter(
        (l) => l.listTypeName === LIST_TYPES_ENUM.emailAddress
      ),

    statusGetter: (state): ListTypeData[] =>
      state.listTypeData.filter(
        (l) => l.listTypeName === LIST_TYPES_ENUM.status
      ),

    suffixGetter: (state): ListTypeData[] =>
      state.listTypeData.filter(
        (l) =>
          l.listTypeName === LIST_TYPES_ENUM.suffix &&
          l.entityName === ENTITY_TYPES_ENUM.user
      ),

    countryGetter: (state): CountryTypeData[] => state.countryData,

    loaderGetter: (state): { show: boolean } => state.loader,
  },
});
